import { isEmpty } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useEffect, useState } from "react";
import CenteredLoading from "../../components/CenteredLoading";
import { USD_CURRENCY_LOCALE_STRING_CONFIG } from "../../components/CurrencySelector";
import InfoIcon from "../../components/icons/InfoIcon";
import classNames from "../../utils/classNames";
import { PriceTiers } from "../../utils/types";
import PricingTable from "./PricingTable";

export interface Props {
  title: string;
  subtitle: JSX.Element;
  description?: JSX.Element[];
  className?: string;
  value: number;
  disabled?: boolean;
  amount: number;
  priceTiers: PriceTiers[] | undefined | null;
  onChange: (value: number) => void;
  onClickButton?: () => Promise<void> | void;
  tooltip?: string | undefined;
  buttonName?: string;
  min: number;
  max: number;
  disabledButton?: boolean;
  buttonNameLoading?: string;
}

const PlansUpgrade: React.FC<Props> = ({
  title,
  subtitle,
  description,
  className,
  value,
  amount,
  disabled = false,
  tooltip,
  priceTiers = [],
  onChange,
  buttonName,
  onClickButton,
  disabledButton = false,
  min,
  max,
  buttonNameLoading
}) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (amount < min) {
      onChange(min);
    }
  }, [amount, min, onChange]);

  return (
    <div
      className={classNames(
        className,
        "flex flex-col w-full border rounded-lg shadow-md bg-white dark:bg-gray-800 dark:border-gray-500"
      )}
    >
      <div className="flex justify-between sm:items-start items-end">
        <div className="flex-1 flex flex-col-reverse sm:flex-row sm:items-center">
          <input
            type="number"
            value={amount + ""}
            max={max}
            min={min}
            className="w-11/12 sm:w-24 border rounded px-4 py-2 text-sm bg-gray-50 border-gray-100 dark:border-0 dark:bg-gray-700 dark:text-gray-300 disabled:opacity-75 disabled:cursor-not-allowed"
            disabled={disabled}
            onChange={(e) => {
              const value = Number(e.target.value);

              if (value > max) return;

              onChange(Number(e.target.value));
            }}
          />
          <span className="sm:ml-2 mb-2 sm:mb-0 font-semibold dark:text-white">
            {title}
          </span>
          {window.innerWidth > 1024 && tooltip && (
            <Tooltip overlay={tooltip}>
              <InfoIcon className="inline-flex h-4 w-4 ml-1" />
            </Tooltip>
          )}
        </div>
        <div className="text-base dark:text-white">
          <div className="text-right flex items-center justify-end mb-2 sm:mb-0">
            <span className="sm:text-lg font-bold">
              {value.toLocaleString("en-US", USD_CURRENCY_LOCALE_STRING_CONFIG)}
            </span>
            <span className="text-sm dark:text-gray-200 ml-1">/month</span>
          </div>
          <div className="text-sm flex items-end flex-col">{subtitle}</div>
        </div>
      </div>

      {description && (
        <div className="text-gray-900 dark:text-gray-200 text-xs font-normal sm:text-sm my-4">
          <ul>
            {description.map((item, index) => (
              <li key={index} className="py-1">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 mb-2 sm:mb-0 sm:mt-4 flex sm:flex-row flex-col justify-between items-start relative">
        <div className={classNames(buttonName && "mt-2.5", "w-full")}>
          {!isEmpty(priceTiers) && (
            <PricingTable
              title={title}
              titleClassName={buttonName && "sm:justify-start"}
              items={priceTiers}
            />
          )}
        </div>

        {buttonName && (
          <span
            className={classNames(
              "flex sm:absolute cursor-pointer items-center sm:right-0 justify-center bg-hotspotty-400 hover:bg-hotspotty-500 text-white rounded-md w-full sm:w-3/12 mx-auto py-2 text-xs sm:ml-10 mt-4 sm:mt-0 focus:outline-none",
              disabledButton && "cursor-not-allowed opacity-50"
            )}
            onClick={async () => {
              if (!onClickButton || loading || disabledButton) return;

              setLoading(true);

              await onClickButton();

              setLoading(false);
            }}
          >
            {loading && <CenteredLoading className="w-4 h-4 mr-2" />}
            {loading && buttonNameLoading ? buttonNameLoading : buttonName}
          </span>
        )}
      </div>
    </div>
  );
};

export default PlansUpgrade;
